import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

export function SearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const run = async (value) => {
    setQ(value);
    if (value.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(value)}`);
      setResults(data);
    } finally {
      setLoading(false);
    }
  };

  const go = (path) => {
    onOpenChange(false);
    setQ("");
    setResults(null);
    navigate(path);
  };

  const groups = results
    ? [
        { label: "Courses", items: results.courses.map((c) => ({ title: c.title, path: `/courses/${c.slug}` })) },
        { label: "Research", items: results.research.map((r) => ({ title: r.title, path: `/research/${r.slug}` })) },
        { label: "Articles", items: results.articles.map((a) => ({ title: a.title, path: `/knowledge/${a.slug}` })) },
        { label: "Glossary", items: results.glossary.map((g) => ({ title: `${g.term} — ${g.abbr || g.category}`, path: `/glossary` })) },
      ].filter((g) => g.items.length)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" data-testid="search-dialog">
        <DialogTitle className="sr-only">Search KRV Prime</DialogTitle>
        <div className="flex items-center gap-3 border-b px-4 py-3.5">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            autoFocus
            data-testid="global-search-input"
            value={q}
            onChange={(e) => run(e.target.value)}
            placeholder="Search courses, research, articles, glossary…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-800 placeholder:text-slate-400"
          />
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {loading && <p className="p-4 text-sm text-slate-400">Searching…</p>}
          {!loading && results && groups.length === 0 && (
            <p className="p-4 text-sm text-slate-400">No results found.</p>
          )}
          {!loading &&
            groups.map((g) => (
              <div key={g.label} className="mb-2">
                <p className="eyebrow text-slate-400 px-3 py-1.5">{g.label}</p>
                {g.items.map((it, i) => (
                  <button
                    key={i}
                    data-testid="search-result-item"
                    onClick={() => go(it.path)}
                    className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-subtle transition-colors"
                  >
                    {it.title}
                  </button>
                ))}
              </div>
            ))}
          {!results && !loading && (
            <p className="p-4 text-sm text-slate-400">Type at least 2 characters to search.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
